// Cabinet/ConsultationList.tsx
import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useProfile} from 'User/ProfileContext';
import BalanceTab, {BalanceItem} from "./BalanceTab";
import CalendarTab from "./CalendarTab";
import {axios, DOMAIN_URL} from 'Auth/axiosConfig';
import {FC, FC as FCC, FCSC, FR, FRC} from 'WideLayout/Layouts';
import ConsultationItem from "Consultation/ConsultationItem";
import {useTheme} from "Theme/ThemeContext";
import {useErrorProcessing} from "Core/components/ErrorProvider";
import {useLocation, useNavigate} from "react-router-dom";
import pprint from '../../Utils/pprint';
import {IConsultation} from '../../Types/psychology/consultation';
import TabButton from "Core/components/elements/Tabs/TabButton";
import PaginatedList from "Core/components/elements/PaginatedList";
import {AuthContext, AuthContextType} from "Auth/AuthContext";
import FindPsychologistButtonsBox from "../FindPsychologistButtonsBox";
import NearestConsultationWidget from "Consultation/NearestConsultationWidget";
import {useMediaQuery} from "@mui/material";
import MyFeedbacksTab from "../ConsultationFeedback/MyFeedbacksTab";
import Modal from "Core/components/elements/Modal/Modal";
import ConsultationFeedbackPage from "../ConsultationFeedback/ConsultationFeedbackPage";
import Head from "Core/components/Head";
import {removeQueryParam} from "../../Utils/uriParams";

interface ConsultationActionsProps {

}


interface BalanceData {
    total_count: number;
    details: BalanceItem[];
}

const ConsultationList: React.FC = () => {
    const {selectedProfile} = useProfile();
    const {isAuthenticated, user} = useContext(AuthContext) as AuthContextType;
    const {plt} = useTheme();
    const [tab, setTab] = useState<string>('scheduled');
    const [nextConsultation, setNextConsultation] = useState<IConsultation | null>(null);
    const [balanceData, setBalanceData] = useState<BalanceData>({total_count: 0, details: []});
    const {byResponse} = useErrorProcessing();
    const navigate = useNavigate();

    const [consultationCanceled, setConsultationCanceled] = useState(false);
    const [hasConsultations, setHasConsultations] = useState<boolean>(true);
    const [balanceUpdated, setBalanceUpdated] = useState<boolean>(false);
    const [noFeedbackConsultations, setNoFeedbackConsultations] = useState<IConsultation[]>([]);
    const isGtMd = useMediaQuery('(min-width: 970px)');

    const refreshBalance = React.useCallback(() => {
        pprint('Refresh balance');
        setBalanceUpdated(prev => !prev);
    }, []);

    useEffect(() => {
        if (isAuthenticated === false) navigate('/auth');
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        if (selectedProfile === 'psychologist') return;
        axios.get('/api/v1/consultations/balance/').then(r => {
            pprint('@@@@@@@');
            pprint('BALANCE');
            pprint('@@@@@@@');
            pprint(r.data);
            setBalanceData(r.data);
        }).catch(e => {
            console.log(e)
            byResponse(e)
        });
    }, [selectedProfile, consultationCanceled, balanceUpdated, byResponse]);

    async function getMyConsultationWithoutFeedback() {
        try {
            const r = await axios.get('/api/v1/consultations/without_feedback/');
            pprint('Without Feedback');
            pprint(r.data);
            setNoFeedbackConsultations(r.data);
        } catch (e) {
            byResponse(e)
        }
    }

    useEffect(() => {
        if (isAuthenticated && user) {
            getMyConsultationWithoutFeedback().then();
        }
    }, [isAuthenticated, user, byResponse, balanceUpdated, consultationCanceled, selectedProfile]);

    const loadConsultations = useCallback(async (page: number): Promise<IConsultation[]> => {
        let endpoint = '';
        if (tab === 'completed') {
            endpoint = '/api/v1/consultations/completed/';
        } else if (selectedProfile === 'psychologist') {
            endpoint = tab === 'scheduled'
                ? '/api/v1/psychologist/scheduled_consultations/'
                : (tab === 'calendar'
                    ? '/api/v1/psychologist/calendar_consultations/'
                    : '/api/v1/psychologist/scheduled_consultations/');
        } else {
            if (tab === 'scheduled') endpoint = '/api/v1/client/scheduled_consultations/';
            else if (tab === 'balance') endpoint = '/api/v1/consultations/balance_data/';
            else if (tab === 'completed') endpoint = '/api/v1/consultations/completed/';
            else if (tab === 'calendar') endpoint = '/api/v1/client/calendar_consultations/';
        }

        const response = await axios.get(endpoint, {
            params: {
                page: page,
                page_size: 10
            }
        });
        pprint('Consultations loaded response: ', response.data);

        const consultations: IConsultation[] = response.data.results || response.data;
        if (tab === 'scheduled' && page === 1) {
            setNextConsultation(
                consultations.find(c => c.date) || null
            );
        }

        if (page === 1) setHasConsultations(consultations.length > 0);

        return consultations;
    }, [selectedProfile, tab]);

    useEffect(() => {
        setHasConsultations(true);
    }, [tab]);

    const handleSelectTab = (tab: string) => {
        setTab(tab);
    };
    const useQuery = () => {
        return new URLSearchParams(useLocation().search);
    };
    const query = useQuery();
    const consultationIdForFeedback = query.get('for_feedback');
    const [modalOpen, setModalOpen] = useState<boolean>(false);

    useEffect(() => {
        const checkFeedbackBeforeOpeningModal = async (id: string) => {
            try {
                const r = await axios.get(`/api/v1/consultations/${id}/get_current_user_feedback/`);
                pprint('checkFeedbackBeforeOpeningModal')
                pprint(r.data)
                if (!r.data) {
                    console.log("Нужен отзыв")
                    removeQueryParam('for_feedback')
                    setModalOpen(true);
                } else {
                    console.log('Feedback already exists for this consultation.');
                }
            } catch (e) {
                byResponse(e)
            }
        };

        if (consultationIdForFeedback) checkFeedbackBeforeOpeningModal(consultationIdForFeedback);
    }, [consultationIdForFeedback]);

    const closeModal = () => {
        setModalOpen(false);
    };

    const textSx = {padding: {xs: '.1rem .4rem'}};

    return (<>
        <Head
            title={'Консультации | Лайф Хелп'}
            description={'Помощь квалифицированных психологов от сервиса Лайф Хелп'}
            robots={'noindex, nofollow'}
            canonicalUrl={`${DOMAIN_URL}/consultations`}
        />
        <FCC h={'100%'} cls="consultation-list">
            <Modal
                isOpen={modalOpen}
                onClose={closeModal}
                closeOnOutsideClick={false}
                closeBtn={true}                                   //ГАЙДА
                cls={'p-3 maxw-800px'}
                bg={`${plt.bg.primary}cc`}>
                <FC maxH={'80vh'}>{consultationIdForFeedback && (
                    <ConsultationFeedbackPage
                        consultationId={consultationIdForFeedback}
                        onClose={closeModal}/>
                )}</FC>
            </Modal>

            <NearestConsultationWidget nextConsultation={nextConsultation}/>
            {noFeedbackConsultations.length > 0 && (
                <FR px={isGtMd ? 0 : 1}>
                    <FCC w={'100%'} g={1} p={2} bg={plt.colors.secondary.lighter} rounded={3} mb={2}>
                        <h5 style={{color: plt.colors.secondary.main}}>Консультации без оценки</h5>
                        {noFeedbackConsultations.map((c, i) => (
                            <ConsultationItem
                                key={i} actionsEnable={true}
                                consultation={c}
                                onConsultationCancel={() => setConsultationCanceled(prev => !prev)}
                                onBalanceChange={refreshBalance}/>
                        ))}
                    </FCC>
                </FR>
            )}
            <FRC g={1} wrap>
                {selectedProfile === 'psychologist' ? <>
                    <TabButton
                        textSx={textSx}
                        active={tab === 'scheduled'}
                        onClick={() => handleSelectTab('scheduled')}>
                        Расписание
                    </TabButton>
                    <TabButton
                        textSx={textSx}
                        active={tab === 'completed'}
                        onClick={() => handleSelectTab('completed')}>
                        Завершенные
                    </TabButton>
                    <TabButton
                        textSx={textSx}
                        active={tab === 'calendar'}
                        onClick={() => handleSelectTab('calendar')}>
                        Календарь
                    </TabButton>
                    <TabButton
                        textSx={textSx}
                        active={tab === 'my-feedbacks'}
                        onClick={() => handleSelectTab('my-feedbacks')}>
                        Мои отзывы
                    </TabButton>
                </> : <>
                    <TabButton
                        textSx={textSx}
                        active={tab === 'scheduled'}
                        onClick={() => handleSelectTab('scheduled')}>
                        Расписание
                    </TabButton>
                    <TabButton
                        textSx={textSx}
                        active={tab === 'balance'}
                        onClick={() => handleSelectTab('balance')}>
                        Баланс ({balanceData.total_count})
                    </TabButton>
                    <TabButton
                        textSx={textSx}
                        active={tab === 'completed'}
                        onClick={() => handleSelectTab('completed')}>
                        Завершенные
                    </TabButton>
                    <TabButton
                        textSx={textSx}
                        active={tab === 'calendar'}
                        onClick={() => handleSelectTab('calendar')}>
                        Календарь
                    </TabButton>
                    <TabButton
                        textSx={textSx}
                        active={tab === 'my-feedbacks'}
                        onClick={() => handleSelectTab('my-feedbacks')}>
                        Мои отзывы
                    </TabButton>
                </>}
            </FRC>

            {tab === 'balance' && selectedProfile !== 'psychologist'
                ? <BalanceTab balanceData={balanceData}/>
                : tab === 'calendar'
                    ? <CalendarTab/>
                    : tab === 'my-feedbacks'
                        ? <MyFeedbacksTab userId={user?.id} isPsychologist={selectedProfile === 'psychologist'}/>
                        : hasConsultations
                            ? <PaginatedList
                                key={tab}
                                resetTrigger={`${selectedProfile}-${tab}`}
                                className="fc gap-2 p-3 h-100 no-scrollbar overflow-y-auto"
                                loadData={loadConsultations}
                                renderItem={(consultation: IConsultation, index: number) => (

                                    <ConsultationItem
                                        key={index}
                                        actionsEnable={true}
                                        consultation={consultation}
                                        onConsultationCancel={() => setConsultationCanceled(prev => !prev)}
                                        onBalanceChange={refreshBalance}/>
                                )}/>
                            : <FCSC grow g={1} p={2} h={'100%'} scroll={'y-auto'}
                                    cls={'no-scrollbar'} textAlign={'center'}>
                                <p>
                                    {tab === 'scheduled'
                                        ? 'У вас нет запланированных консультаций.'
                                        : 'У вас нет завершенных консультаций.'}
                                </p>
                                <FindPsychologistButtonsBox cls={'maxw-250px mx-auto'}/>
                            </FCSC>
            }
        </FCC>
    </>);
};

export default ConsultationList;
